import React, { useState, useRef } from 'react'
import { Modal, Button, Icon } from 'antd'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import 'antd/dist/antd.css'

import './ImagePreview.scss'

const ImagePreview = props => {
  const [visible, setVisible] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [initialScale, setInitialScale] = useState(1)
  const contentRef = useRef(null)

  const showModal = () => {
    setVisible(true)
  }

  const handleCancel = () => {
    setVisible(false)
    resetRotation()
  }

  const rotateClockwise = () => {
    setRotation(prevRotation => prevRotation + 90)
  }

  const rotateCounterclockwise = () => {
    setRotation(prevRotation => prevRotation - 90)
  }

  const resetRotation = () => {
    setRotation(0)
  }

  const handleImageLoad = (e, { setTransform }) => {
    const img = e.target
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Calculate scale to fit image (object-fit: contain behavior)
    const scaleX = viewportWidth / img.naturalWidth
    const scaleY = viewportHeight / img.naturalHeight
    const fitScale = Math.min(scaleX, scaleY, 1) // Don't scale up beyond natural size if it already fits

    setInitialScale(fitScale)

    // Center the image initially with the calculated scale
    // We use setTransform to ensure it's applied correctly on mount
    const x = (viewportWidth - img.naturalWidth * fitScale) / 2
    const y = (viewportHeight - img.naturalHeight * fitScale) / 2
    setTransform(x, y, fitScale, 0)
  }

  return (
    <>
      <img
        key={props.src}
        alt={props.alt || 'Preview'}
        src={props.src}
        crossOrigin={props.crossorigin}
        onClick={showModal}
        className={props.className ? props.className : 'shl-image-preview'}
        style={{
          cursor: 'pointer',
          maxWidth: '100%',
          opacity: props.opacity ? props.opacity : 1,
          ...props.style
        }}
      />

      <Modal
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width="100vw"
        style={{
          maxWidth: '100vw',
          margin: 0,
          top: 0,
          padding: 0
        }}
        bodyStyle={{
          height: '100vh',
          padding: 0
        }}
        wrapClassName="shl-image-preview-modal"
        destroyOnClose
      >
        <TransformWrapper
          initialScale={initialScale}
          minScale={0.05}
          maxScale={10}
          centerOnInit={true}
          limitToBounds={true} // Restrict panning to viewport bounds as requested
          wheel={{ step: 0.1 }}
          pinch={{ step: 0.1 }}
          doubleClick={{ disabled: true }}
          alignmentAnimation={{ disabled: true }} // Disable bouncy alignment for more precise control
        >
          {transformProps => {
            const {
              zoomIn,
              zoomOut,
              resetTransform,
              setTransform
            } = transformProps
            return (
              <div className="image-preview-container">
                <div className="shl-tools">
                  <Button onClick={() => zoomIn()}>
                    <Icon type="zoom-in" />
                  </Button>
                  <Button onClick={() => zoomOut()}>
                    <Icon type="zoom-out" />
                  </Button>
                  <Button onClick={rotateClockwise}>
                    <Icon type="redo" />
                  </Button>
                  <Button onClick={rotateCounterclockwise}>
                    <Icon type="undo" />
                  </Button>
                  <Button
                    className="btn-reset"
                    onClick={() => {
                      resetRotation()
                      resetTransform()
                    }}
                  >
                    Reset
                  </Button>
                </div>

                <TransformComponent
                  wrapperStyle={{
                    width: '100vw',
                    height: '100vh'
                  }}
                  contentStyle={{
                    width: 'auto',
                    height: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <div
                    ref={contentRef}
                    className="image-wrapper"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transformOrigin: 'center center'
                    }}
                  >
                    <img
                      alt={props.alt || 'Preview'}
                      src={props.src}
                      crossOrigin={props.crossorigin}
                      onLoad={e => handleImageLoad(e, transformProps)}
                      style={{
                        maxWidth: 'none',
                        maxHeight: 'none',
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: 'center center',
                        transition:
                          rotation === 0
                            ? 'none'
                            : 'transform 0.3s ease-in-out',
                        userSelect: 'none',
                        WebkitUserDrag: 'none'
                      }}
                    />
                  </div>
                </TransformComponent>
              </div>
            )
          }}
        </TransformWrapper>
      </Modal>
    </>
  )
}

export default ImagePreview
